import { Alert, Box } from "@mui/material";
import {
  applySpec,
  chain,
  filter,
  identity,
  map,
  mapObjIndexed,
  pathOr,
  pipe,
  prop,
  sortBy,
  toLower,
  uniq,
} from "ramda";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "../../components/Layout";
import { MsgProvider } from "../../components/Msg";
import { useMsg } from "../../components/Msg/Msg";
import { ScheduledSessionsCard } from "../../components/ScheduledSessions/ScheduledSessions";
import { isString } from "../../utils/string.utils";
import { useAuth } from "../Authorization";
import { QueryRenderer } from "../QM/QueryRenderer";
import {
  useCoachesQuery,
  useUserUpcomingSessionsQuery,
  useYourCoachQuery,
} from "./api";
import { CoachCard } from "./CoachCard";
import { CoachesFilter, INITIAL_FILTER } from "./CoachesFilter";
import { filterPredicate } from "./coachesFilter.utils";
import { CoachesRightMenu } from "./CoachesRightMenu";
import { useFavorites } from "./Favorites/useFavorites";
import { coachesMessages } from "./messages";

const allCoachesFilter = INITIAL_FILTER();
const emptyArrayStatic = [];

export function CoachesPageInner() {
  const msg = useMsg({ dict: coachesMessages });
  const { favoritesMaybe } = useFavorites({ id: "pending-not-used" });

  let [searchParams] = useSearchParams();
  const initialDisplayFavorites = !!searchParams.get("displayFavorites");
  const [coachesFilter, setCoachesFilter] = useState(
    INITIAL_FILTER({ displayFavorites: initialDisplayFavorites }),
  );
  // Currently filtered on FE to be able to filter favorites
  // TODO: discuss BE filtering
  // const coachesQuery = useCoachesQuery({ filter: coachesFilter });
  const allCoachesQuery = useCoachesQuery({
    filter: allCoachesFilter,
  });

  const allPossiblyVisibleCoaches =
    allCoachesQuery.data?.content || emptyArrayStatic;
  const {
    supportedLanguages,
    supportedFields,
    supportedTimezones,
    supportedRates,
    supportedPrimaryRoles,
  } = useMemo(() => {
    // We want to display only options for which we have some results
    // omitKeys is needed, as e.g., when calculating supportedLanguages and one language is selected, other languages would disappear from the options
    const applyFilterExcept = (omitKeys) =>
      filter(filterPredicate({ coachesFilter, favoritesMaybe, omitKeys }));
    return pipe(
      applySpec({
        supportedLanguages: pipe(
          applyFilterExcept(["languages"]),
          chain(prop("languages")),
        ),
        supportedFields: pipe(
          applyFilterExcept(["fields"]),
          chain(prop("fields")),
        ),
        supportedTimezones: pipe(
          applyFilterExcept(["timezones"]),
          map(prop("timeZone")),
          filter(isString), // on prod, one coach has timeZone: null
          sortBy(toLower),
        ),
        supportedRates: pipe(applyFilterExcept(["prices"]), map(prop("rate"))),
        supportedPrimaryRoles: pipe(
          applyFilterExcept(["primaryRoles"]),
          chain(prop("primaryRoles")),
        ),
      }),
      mapObjIndexed(uniq),
    )(allPossiblyVisibleCoaches);
  }, [allPossiblyVisibleCoaches, coachesFilter, favoritesMaybe]);

  return (
    <Layout
      header={{ heading: msg("coaches.heading") }}
      rightMenuContent={<CoachesRightMenu />}
    >
      <CoachesFilter
        filter={coachesFilter}
        setFilter={setCoachesFilter}
        supportedLanguages={supportedLanguages}
        supportedFields={supportedFields}
        supportedTimezones={supportedTimezones}
        supportedRates={supportedRates}
        supportedPrimaryRoles={supportedPrimaryRoles}
        favoritesDisplayed
        favoritesDisabled={!favoritesMaybe?.length}
        allowEmptyFavorites={initialDisplayFavorites}
        disabled={!allPossiblyVisibleCoaches.length}
      />
      <QueryRenderer
        query={allCoachesQuery}
        loaderEl={<CoachCard isLoading sx={{ my: 3 }} />}
        success={pipe(
          pathOr([], ["data", "content"]),
          filter(filterPredicate({ coachesFilter, favoritesMaybe })),
          map((coach) => (
            <CoachCard
              key={coach.username}
              coach={coach}
              withContact
              sx={{ my: 3 }}
            />
          )),
        )}
        errored={({ error }) =>
          error && (
            <Alert severity="error" sx={{ wordWrap: "break-word", mt: 3 }}>
              {error?.message || "Oops!"}
            </Alert>
          )
        }
      />
      {/* <QueryRenderer query={coachesQuery} loaderEl={<CoachCard isLoading sx={{ my: 3 }} />} success={({ data: { content = [] } }) => content.map((coach, i) => ( <CoachCard key={coach.username} coach={coach} withContact sx={{ my: 3 }} /> )) } /> */}
      <Box sx={{ height: "1px" }} />
    </Layout>
  );
}

const YourCoachPageInner = () => {
  const msg = useMsg({ dict: coachesMessages });
  const yourCoachQuery = useYourCoachQuery();
  const userUpcomingSessionsQuery = useUserUpcomingSessionsQuery();

  return (
    <Layout
      header={{ heading: msg("your-coach.heading") }}
      rightMenuContent={<CoachesRightMenu />}
    >
      <QueryRenderer
        query={yourCoachQuery}
        loaderEl={<CoachCard isLoading sx={{ my: 3 }} />}
        success={({ data: coach }) => (
          <CoachCard coach={coach} withContact sx={{ my: 3 }} />
        )}
      />
      <QueryRenderer
        query={userUpcomingSessionsQuery}
        loading={() => null}
        success={({ data }) => (
          <ScheduledSessionsCard
            data={data}
            firstRowExtraInfo={msg("your-coach.session-link-info")}
          />
        )}
      />
    </Layout>
  );
};

export function CoachesPage() {
  const { userQuery } = useAuth();

  return (
    <MsgProvider messages={coachesMessages}>
      {userQuery.data.coach ? <YourCoachPageInner /> : <CoachesPageInner />}
    </MsgProvider>
  );
}
